// src/lib/metrics/returns.ts
/**
 * Returns calculations - converting prices to returns and derived metrics
 */

import type { Series, ValidSeries } from './types';
import { TRADING_DAYS } from './types';
import { filterValid, mean } from './primitives';

// ============================================================================
// PRICE TO RETURNS CONVERSION
// ============================================================================

/**
 * Convert price series to simple daily returns
 * Returns array is one element shorter than input
 * Preserves NaN positions for date alignment
 * 
 * @param prices - Array of prices (oldest to newest)
 * @returns Array of daily returns as decimals (e.g., 0.05 for 5%)
 */
export function toReturns(prices: Series): number[] {
  if (prices.length < 2) return [];
  
  const returns: number[] = [];
  
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1];
    const curr = prices[i];
    
    if (
      prev !== null && prev !== undefined && 
      curr !== null && curr !== undefined &&
      prev > 0 && Number.isFinite(prev) && Number.isFinite(curr)
    ) {
      returns.push((curr / prev) - 1);
    } else {
      returns.push(NaN);
    }
  }
  
  return returns;
}

/**
 * Convert price series to log returns
 * More suitable for statistical analysis and compounding
 * Preserves NaN positions for date alignment
 */
export function toLogReturns(prices: Series): number[] {
  if (prices.length < 2) return [];
  
  const returns: number[] = [];
  
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1];
    const curr = prices[i];
    
    if (
      prev !== null && prev !== undefined && 
      curr !== null && curr !== undefined &&
      prev > 0 && curr > 0
    ) {
      returns.push(Math.log(curr / prev));
    } else {
      returns.push(NaN);
    }
  }
  
  return returns;
}

/**
 * Convert price series to returns, preserving alignment with NaN
 * Useful when you need returns array to match dates array length
 * 
 * @param prices - Array of prices
 * @returns Array of returns with NaN at index 0 to maintain alignment
 */
export function toReturnsAligned(prices: Series): number[] {
  const returns = toReturns(prices);
  return [NaN, ...returns]; // Prepend NaN to align with prices
}

// ============================================================================
// CUMULATIVE RETURNS
// ============================================================================

/**
 * Convert returns series to cumulative indexed series
 * Useful for charting portfolio growth
 * 
 * @param returns - Array of period returns as decimals
 * @param startValue - Initial index value (default 100)
 * @returns Cumulative series starting at startValue
 */
export function toCumulative(
  returns: Series,
  startValue: number = 100
): number[] {
  const valid = filterValid(returns);
  if (valid.length === 0) return [startValue];
  
  const cumulative: number[] = [startValue];
  let current = startValue;
  
  for (const r of valid) {
    current *= (1 + r);
    cumulative.push(current);
  }
  
  return cumulative;
}

/**
 * Convert prices to cumulative returns as percentages (starting at 0)
 * Useful for charting relative performance
 * 
 * @param prices - Array of prices
 * @returns Array of cumulative returns in percent [0, 5, -3, ...]
 *          First element is always 0, length matches input
 */
export function pricesToCumulativePercent(prices: Series): number[] {
  if (prices.length < 2) return prices.map(() => 0);
  
  const cumulative: number[] = [0]; // Start at 0%
  let c = 0; // Running cumulative return as decimal
  
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1];
    const curr = prices[i];
    
    if (
      prev !== null && prev !== undefined && Number.isFinite(prev) && prev > 0 &&
      curr !== null && curr !== undefined && Number.isFinite(curr)
    ) {
      const r = (curr / prev) - 1;
      c = (1 + c) * (1 + r) - 1;
      cumulative.push(c * 100);
    } else {
      // Carry forward previous cumulative for missing data
      cumulative.push(c * 100);
    }
  }
  
  return cumulative;
}

/**
 * Calculate total return from a series of period returns
 * 
 * @param returns - Array of period returns as decimals
 * @returns Total compounded return as decimal (e.g., 0.25 for 25%)
 */
export function totalReturn(returns: Series): number | null {
  const valid = filterValid(returns);
  if (valid.length === 0) return null;
  
  let cumulative = 1;
  for (const r of valid) {
    cumulative *= (1 + r);
  }
  
  return cumulative - 1;
}

/**
 * Calculate total return from prices directly
 */
export function totalReturnFromPrices(prices: Series): number | null {
  const valid = filterValid(prices);
  if (valid.length < 2) return null;
  
  const first = valid[0]!;
  const last = valid[valid.length - 1]!;
  
  if (first <= 0) return null;
  return (last / first) - 1;
}

// ============================================================================
// ANNUALIZED RETURNS
// ============================================================================

/**
 * Annualize a return based on number of periods
 * Uses geometric compounding
 * 
 * @param totalReturn - Total return as decimal
 * @param periods - Number of periods in the total return
 * @param periodsPerYear - Periods in a year (default: 365 for crypto daily)
 */
export function annualizeReturn(
  totalReturn: number,
  periods: number,
  periodsPerYear: number = TRADING_DAYS.CRYPTO
): number | null {
  if (periods <= 0 || !Number.isFinite(totalReturn)) return null;
  
  // (1 + total_return)^(periods_per_year / periods) - 1
  const growth = 1 + totalReturn;
  if (growth <= 0) return null; // Can't annualize negative total return
  
  return Math.pow(growth, periodsPerYear / periods) - 1;
}

/**
 * Calculate annualized return from daily returns
 */
export function annualizedReturnFromDaily(
  dailyReturns: Series,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const total = totalReturn(dailyReturns);
  if (total === null) return null;
  
  const periods = filterValid(dailyReturns).length;
  return annualizeReturn(total, periods, tradingDays);
}

/**
 * Calculate CAGR (Compound Annual Growth Rate) from prices
 */
export function cagr(
  prices: Series,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const valid = filterValid(prices);
  if (valid.length < 2) return null;
  
  const first = valid[0]!;
  const last = valid[valid.length - 1]!;
  const periods = valid.length - 1;
  
  if (first <= 0 || last <= 0) return null;
  
  // CAGR = (ending/beginning)^(1/years) - 1
  const years = periods / tradingDays;
  return Math.pow(last / first, 1 / years) - 1;
}

// ============================================================================
// EXCESS RETURNS
// ============================================================================

/**
 * Calculate excess returns over a risk-free rate
 * 
 * @param returns - Array of period returns
 * @param riskFreeRate - Risk-free rate per period (same frequency as returns)
 */
export function excessReturns(
  returns: Series,
  riskFreeRate: number = 0
): number[] {
  return filterValid(returns).map(r => r - riskFreeRate);
}

/**
 * Calculate active returns (returns vs benchmark)
 * 
 * @param returns - Portfolio returns
 * @param benchmarkReturns - Benchmark returns
 */
export function activeReturns(
  returns: Series,
  benchmarkReturns: Series
): number[] {
  const r = filterValid(returns);
  const b = filterValid(benchmarkReturns);
  
  const n = Math.min(r.length, b.length);
  const rAligned = r.slice(-n);
  const bAligned = b.slice(-n);
  
  return rAligned.map((ret, i) => ret - bAligned[i]!);
}

// ============================================================================
// PERIOD AGGREGATION
// ============================================================================

/**
 * Compound daily returns to weekly returns
 */
export function compoundToWeekly(dailyReturns: ValidSeries): number[] {
  const weeklyReturns: number[] = [];
  
  for (let i = 0; i < dailyReturns.length; i += 5) {
    const weekSlice = dailyReturns.slice(i, Math.min(i + 5, dailyReturns.length));
    let weekReturn = 1;
    for (const r of weekSlice) {
      weekReturn *= (1 + r);
    }
    weeklyReturns.push(weekReturn - 1);
  }
  
  return weeklyReturns;
}

/**
 * Compound daily returns to monthly returns (approx 21 trading days)
 */
export function compoundToMonthly(
  dailyReturns: ValidSeries,
  daysPerMonth: number = 21
): number[] {
  const monthlyReturns: number[] = [];
  
  for (let i = 0; i < dailyReturns.length; i += daysPerMonth) {
    const monthSlice = dailyReturns.slice(i, Math.min(i + daysPerMonth, dailyReturns.length));
    let monthReturn = 1;
    for (const r of monthSlice) {
      monthReturn *= (1 + r);
    }
    monthlyReturns.push(monthReturn - 1);
  }
  
  return monthlyReturns;
}

// ============================================================================
// RETURN STATISTICS
// ============================================================================

/**
 * Calculate average daily return (arithmetic mean)
 */
export function averageReturn(returns: Series): number | null {
  return mean(returns);
}

/**
 * Calculate win rate (percentage of positive returns)
 */
export function winRate(returns: Series): number | null {
  const valid = filterValid(returns);
  if (valid.length === 0) return null;
  
  const wins = valid.filter(r => r > 0).length;
  return wins / valid.length;
}

/**
 * Calculate average win and average loss
 */
export function winLossStats(returns: Series): {
  avgWin: number | null;
  avgLoss: number | null;
  winLossRatio: number | null;
} {
  const valid = filterValid(returns);
  
  const wins = valid.filter(r => r > 0);
  const losses = valid.filter(r => r < 0);
  
  const avgWin = wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : null;
  const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((a, b) => a + b, 0) / losses.length) : null;
  
  const winLossRatio = avgWin !== null && avgLoss !== null && avgLoss !== 0
    ? avgWin / avgLoss
    : null;
  
  return { avgWin, avgLoss, winLossRatio };
}

